// src/stores/fetchProgressStore.ts
/**
 * Global fetch-progress store (Zustand v5).
 *
 * Tracks in-flight "big" API fetches so the FetchProgressBar can show users
 * what is happening (and that the backend is still alive) instead of a
 * spinner that reads as "crashed" during multi-second pulls.
 *
 * Two modes per job:
 *  - Indeterminate (default): pagesDone stays 0 → the bar sweeps; elapsed
 *    seconds reassure the user.
 *  - Determinate: the adapter reports each finished page via tickPage();
 *    when a page estimate is available setEstimate() drives a real fill.
 *
 * Selector contract (perf): components subscribe via useActiveProgress()
 * which returns the OLDEST active job only — one bar, one job. Progress
 * ticks only mutate job fields (page boundaries, not per row), so table
 * rows never re-render from progress updates (AGENTS.md §2.5 Layer 1).
 *
 * Reference pattern: src/stores/uiStore.ts (plain create, no devtools).
 */

import { create } from 'zustand';

export interface FetchProgressJob {
  /** Unique job id (withProgress generates one). */
  id: string;
  /** i18n key describing what is loading, e.g. 'fetchProgress.reservations'. */
  labelKey: string;
  labelOpts?: Record<string, unknown>;
  /** Pages completed (determinate mode; 0 in indeterminate mode). */
  pagesDone: number;
  /** True once an estimated page total is known → determinate fill. */
  pageKnown: boolean;
  estPages: number;
  /** Rows fetched so far (display only). */
  rowsDone: number;
  /** Date.now() at job start — drives the elapsed-seconds text. */
  startedAt: number;
}

interface FetchProgressState {
  jobs: Record<string, FetchProgressJob>;
  /** Insertion order of job ids — "oldest active job" selection. */
  order: string[];
  startJob: (id: string, labelKey: string, labelOpts?: Record<string, unknown>) => void;
  /** One page finished. Called on page boundaries only (not per row). */
  tickPage: (id: string, rowsDone: number) => void;
  /** Provide/refresh the estimated total page count for a determinate fill. */
  setEstimate: (id: string, estPages: number) => void;
  /** Complete and remove a job. Idempotent (safe in finally blocks). */
  endJob: (id: string) => void;
}

export const useFetchProgress = create<FetchProgressState>()((set) => ({
  jobs: {},
  order: [],
  startJob: (id, labelKey, labelOpts) =>
    set((state) => {
      if (state.jobs[id]) return state; // idempotent — same job restarted
      const job: FetchProgressJob = {
        id,
        labelKey,
        labelOpts,
        pagesDone: 0,
        pageKnown: false,
        estPages: 0,
        rowsDone: 0,
        startedAt: Date.now(),
      };
      return { jobs: { ...state.jobs, [id]: job }, order: [...state.order, id] };
    }),
  tickPage: (id, rowsDone) =>
    set((state) => {
      const job = state.jobs[id];
      if (!job) return state;
      return {
        jobs: {
          ...state.jobs,
          [id]: { ...job, pagesDone: job.pagesDone + 1, rowsDone },
        },
      };
    }),
  setEstimate: (id, estPages) =>
    set((state) => {
      const job = state.jobs[id];
      if (!job || job.pageKnown) return state;
      return {
        jobs: {
          ...state.jobs,
          [id]: { ...job, pageKnown: true, estPages: Math.max(1, estPages) },
        },
      };
    }),
  endJob: (id) =>
    set((state) => {
      if (!state.jobs[id]) return state;
      const jobs = { ...state.jobs };
      delete jobs[id];
      return { jobs, order: state.order.filter((x) => x !== id) };
    }),
}));

/** The oldest active job, or null — the single job the bar renders. */
export function selectActiveJob(state: { jobs: Record<string, FetchProgressJob>; order: string[] }): FetchProgressJob | null {
  for (const id of state.order) {
    if (state.jobs[id]) return state.jobs[id];
  }
  return null;
}

/** Number of jobs queued behind the active one (for a future "+n weitere"). */
export function selectQueuedCount(state: { jobs: Record<string, FetchProgressJob>; order: string[] }): number {
  return Math.max(0, state.order.length - 1);
}

let jobCounter = 0;
/**
 * Wrap a paginated fetch: starts a job and returns an onProgress callback +
 * a done() to call in the consumer's finally block.
 *
 * Usage:
 *   const { onProgress, done } = withProgress('fetchProgress.reservations');
 *   try {
 *     await pms.fetchReservations(from, to, 'Arrival', expand, '', prop, pages, { onProgress });
 *   } finally { done(); }
 */
export function withProgress(labelKey: string, labelOpts?: Record<string, unknown>) {
  const id = `fp-${Date.now()}-${jobCounter++}`;
  useFetchProgress.getState().startJob(id, labelKey, labelOpts);
  return {
    id,
    onProgress: (info: { page: number; rows: number }) => {
      useFetchProgress.getState().tickPage(id, info.rows);
    },
    onEstimate: (estPages: number) => {
      useFetchProgress.getState().setEstimate(id, estPages);
    },
    done: () => {
      useFetchProgress.getState().endJob(id);
    },
  };
}
